// DEV feedback loop — owner logs feedback in-app; Claude reads it from the shared store
// (feedback/tickets.json via /__feedback), fixes, replies, and marks resolved. Owner confirms
// (→ history, hidden) or replies (→ reopens, stays in the list). Admin + Dev-mode gated.
// Self-contained inline styles so it renders identically under the shell or the legacy chrome.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDevMode, FEEDBACK_BASE } from './devmode';
import { formatDate } from '../utils/format';

const C = {
  ink: '#1b2a22', mut: '#6b7a70', line: '#e4e8e3', panel: '#fff', bg: '#f7faf8',
  green: '#2d6a4f', greenBg: '#e8f1ec', amber: '#92650e', amberBg: '#fdf3d7',
  claudeBg: '#eef2f7', ownerBg: '#f1f7f3',
};
const rel = (ts) => {
  const s = Math.max(1, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return s + 's ago';
  const m = Math.round(s / 60); if (m < 60) return m + 'm ago';
  const h = Math.round(m / 60); if (h < 24) return h + 'h ago';
  return formatDate(ts);
};

// ── Hoisted (module-level) components — stable identities so React never remounts a
//    ticket subtree on re-render/poll. Defining these inside FeedbackWidget was making the
//    reply inputs lose focus on every keystroke ("sluggish"). ──
const Chip = ({ tone, children }) => (
  <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase',
    padding: '2px 8px', borderRadius: 20,
    background: tone === 'green' ? C.greenBg : tone === 'amber' ? C.amberBg : '#eef1ee',
    color: tone === 'green' ? C.green : tone === 'amber' ? C.amber : C.mut }}>{children}</span>
);

const Thread = ({ t }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '8px 0' }}>
    {t.thread.map((m, i) => (
      <div key={i} style={{ alignSelf: m.author === 'claude' ? 'flex-start' : 'flex-end', maxWidth: '92%' }}>
        <div style={{ fontSize: 13, lineHeight: 1.45, color: C.ink, whiteSpace: 'pre-wrap',
          background: m.author === 'claude' ? C.claudeBg : C.ownerBg, borderRadius: 9,
          padding: '7px 10px', border: `1px solid ${C.line}` }}>{m.body}</div>
        <div style={{ fontSize: 10, color: C.mut, marginTop: 2, textAlign: m.author === 'claude' ? 'left' : 'right' }}>
          {m.author === 'claude' ? '🤖 Claude' : '🧑 You'} · {rel(m.ts)}
        </div>
      </div>
    ))}
  </div>
);

const Card = ({ t, active, busy, replyVal, onReplyChange, onReply, onConfirm, onPrioritize, onDiscard,
  onHold, onRevive, onGoto, editing, editVal, onEditChange, onStartEdit, onSaveEdit, onCancelEdit, onFocus, onBlur }) => {
  const live = t.status === 'open' || t.status === 'resolved';   // in my working queue
  const held = t.status === 'held';
  const chipTone = t.status === 'resolved' || t.status === 'confirmed' ? 'green' : t.status === 'amber' ? 'amber' : (t.status === 'discarded' || held) ? 'mut' : 'amber';
  const chipLabel = t.status === 'resolved' ? 'Fixed — confirm?' : t.status === 'confirmed' ? 'Confirmed ✓'
    : t.status === 'discarded' ? 'Discarded' : held ? 'On hold' : 'Awaiting fix';
  return (
  <div style={{ border: `1px solid ${active ? C.green : C.line}`, borderLeft: active ? `3px solid ${C.green}` : t.priorityAt ? '3px solid #d9a441' : `1px solid ${C.line}`, borderRadius: 11, background: active ? C.greenBg : C.panel, padding: '11px 13px', marginBottom: 10, boxShadow: active ? '0 0 0 2px rgba(45,106,79,.12)' : 'none', opacity: held ? 0.85 : 1 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
      <Chip tone={chipTone}>{chipLabel}</Chip>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {active && <span style={{ fontSize: 10.5, fontWeight: 700, color: C.green }}>⚙️ working</span>}
        {(live || held) && <button data-borderless="link" onClick={() => onStartEdit(t)} disabled={busy} title="Edit ticket text"
          style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12.5, lineHeight: 1, color: C.mut }}>✏️</button>}
        {live && <button data-borderless="link" onClick={() => onPrioritize(t)} disabled={busy}
          title={t.priorityAt ? 'Prioritized — click to unpin' : 'Move to top of queue'}
          style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, lineHeight: 1,
            color: t.priorityAt ? '#d9a441' : C.mut }}>{t.priorityAt ? '★ Top' : '☆ Prioritize'}</button>}
        <span style={{ fontSize: 11, color: C.mut, fontFamily: 'ui-monospace,monospace' }}>{t.id}</span>
      </div>
    </div>
    <div onClick={() => t.page && onGoto(t.page)} title={t.page ? 'Go to this page' : undefined} style={{ fontSize: 11, marginTop: 4, wordBreak: 'break-all', color: t.page ? C.green : C.mut, cursor: t.page ? 'pointer' : 'default', textDecoration: t.page ? 'underline' : 'none' }}>{t.page ? '📍 ' + t.page : '🌐 General'}</div>
    {editing ? (
      <div style={{ marginTop: 8 }}>
        <textarea value={editVal} onChange={e => onEditChange(e.target.value)} onFocus={onFocus} onBlur={onBlur}
          rows={3} style={{ ...inp, width: '100%', resize: 'vertical', minHeight: 56 }} />
        <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
          <button onClick={onCancelEdit} disabled={busy} style={{ ...btn, ...btnGhost }}>Cancel</button>
          <button onClick={() => onSaveEdit(t)} disabled={busy} style={{ ...btn, background: C.green, color: '#fff', border: `1px solid ${C.green}` }}>Save</button>
        </div>
      </div>
    ) : <Thread t={t} />}
    {!editing && live && (
      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        {t.status === 'resolved' && <button onClick={() => onConfirm(t)} disabled={busy}
          style={{ ...btn, background: C.green, color: '#fff', border: `1px solid ${C.green}` }}>Confirm ✓</button>}
        {t.status === 'open' && <button onClick={() => onConfirm(t)} disabled={busy} title="Mark done — moves to history"
          style={{ ...btn, background: '#fff', color: C.green, border: `1px solid ${C.green}` }}>Mark resolved</button>}
        <button onClick={() => onHold(t)} disabled={busy} title="Save for later — moves to On hold"
          style={{ ...btn, ...btnGhost }}>Hold</button>
        <button onClick={() => onDiscard(t)} disabled={busy} title="Drop this ticket — won't pursue"
          style={{ ...btn, background: '#fff', color: '#a04435', border: '1px solid #e3cdc7' }}>Discard</button>
      </div>
    )}
    {!editing && held && (
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button onClick={() => onRevive(t)} disabled={busy}
          style={{ ...btn, background: C.green, color: '#fff', border: `1px solid ${C.green}` }}>Revive</button>
        <button onClick={() => onDiscard(t)} disabled={busy}
          style={{ ...btn, background: '#fff', color: '#a04435', border: '1px solid #e3cdc7' }}>Discard</button>
      </div>
    )}
    {!editing && live && <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
      <input value={replyVal || ''} onChange={e => onReplyChange(t.id, e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onReply(t); }}
        onFocus={onFocus} onBlur={onBlur}
        placeholder={t.status === 'resolved' ? 'Not fixed? reply to reopen…' : 'Add detail…'}
        style={inp} />
      <button onClick={() => onReply(t)} disabled={busy} style={{ ...btn, ...btnGhost }}>Reply</button>
    </div>}
  </div>
  );
};

export default function FeedbackWidget() {
  const devOn = useDevMode();
  const { isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [db, setDb] = useState({ tickets: [], seq: 0 });
  const [draft, setDraft] = useState('');
  const [scope, setScope] = useState('page');   // 'page' | 'general'
  const [replies, setReplies] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showHeld, setShowHeld] = useState(() => { try { return localStorage.getItem('fb.showHeld') !== '0'; } catch { return true; } });
  const [pageOnly, setPageOnly] = useState(false);
  const [awaitingOnly, setAwaitingOnly] = useState(false);   // fb-119: filter to tickets awaiting the owner's reply
  const [fixedOnly, setFixedOnly] = useState(false);         // fb-119: isolate Fixed (resolved) tickets; composes with the others
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState('');
  useEffect(() => { try { localStorage.setItem('fb.showHeld', showHeld ? '1' : '0'); } catch { /* ignore */ } }, [showHeld]);   // fb-119: persist On-hold minimization
  const pollRef = useRef(null);
  const lastJsonRef = useRef('');
  const typingRef = useRef(false);   // pause polling while a field is focused (no mid-type refresh)

  const load = useCallback(async () => {
    if (typingRef.current) return;   // don't clobber state / steal focus mid-typing
    try {
      const r = await fetch(`${FEEDBACK_BASE}/__feedback`, { headers: { 'Cache-Control': 'no-store' } });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const txt = await r.text();
      setErr('');
      if (txt === lastJsonRef.current) return;   // unchanged → skip re-render (no flashing)
      lastJsonRef.current = txt;
      setDb(JSON.parse(txt));
    } catch (e) { setErr('Feedback service unreachable' + (FEEDBACK_BASE ? ' (' + FEEDBACK_BASE + ')' : '')); }
  }, []);

  const post = useCallback(async (payload) => {
    setBusy(true);
    try {
      const r = await fetch(`${FEEDBACK_BASE}/__feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const txt = await r.text();
      lastJsonRef.current = txt;
      setDb(JSON.parse(txt)); setErr('');
    } catch (e) { setErr('Could not save — service unreachable'); }
    finally { setBusy(false); }
  }, []);

  // poll: fast when open, slow when closed (keeps the badge fresh)
  useEffect(() => {
    if (!(isAdmin && devOn)) return;
    load();
    pollRef.current = setInterval(load, open ? 6000 : 25000);
    return () => clearInterval(pollRef.current);
  }, [isAdmin, devOn, open, load]);

  const onReplyChange = useCallback((id, v) => setReplies(r => ({ ...r, [id]: v })), []);
  const onFocus = useCallback(() => { typingRef.current = true; }, []);
  const onBlur = useCallback(() => { typingRef.current = false; }, []);

  const send = useCallback(async () => {
    const body = draft.trim(); if (!body) return;
    await post({ action: 'create', page: scope === 'page' ? location.pathname + location.search : '', body });
    setDraft('');
  }, [draft, scope, location, post]);

  const onReply = useCallback(async (t) => {
    const body = (replies[t.id] || '').trim(); if (!body) return;
    await post({ action: 'reply', id: t.id, author: 'owner', body });
    setReplies(r => ({ ...r, [t.id]: '' }));
  }, [replies, post]);

  const onConfirm = useCallback((t) => post({ action: 'confirm', id: t.id }), [post]);
  const onGoto = useCallback((page) => { if (!page) return; const url = /[?&]shell=/.test(page) ? page : page + (page.includes('?') ? '&' : '?') + 'shell=v2'; navigate(url); setOpen(false); }, [navigate]);
  const onPrioritize = useCallback((t) => post({ action: 'prioritize', id: t.id }), [post]);
  const onDiscard = useCallback((t) => { if (window.confirm('Discard this ticket? It moves to history and I won\'t pursue it.')) post({ action: 'discard', id: t.id }); }, [post]);
  const onHold = useCallback((t) => post({ action: 'hold', id: t.id }), [post]);
  const onRevive = useCallback((t) => post({ action: 'revive', id: t.id }), [post]);
  const onStartEdit = useCallback((t) => { setEditingId(t.id); setEditVal((t.thread[0] && t.thread[0].body) || ''); }, []);
  const onEditChange = useCallback((v) => setEditVal(v), []);
  const onCancelEdit = useCallback(() => { setEditingId(null); setEditVal(''); }, []);
  const onSaveEdit = useCallback(async (t) => { await post({ action: 'edit', id: t.id, body: editVal }); setEditingId(null); setEditVal(''); }, [post, editVal]);
  const [pinged, setPinged] = useState(false);
  const onPing = useCallback(async () => { await post({ action: 'ping' }); setPinged(true); setTimeout(() => setPinged(false), 4000); }, [post]);

  if (!(isAdmin && devOn)) return null;

  const active = db.tickets.filter(t => t.status !== 'confirmed' && t.status !== 'discarded');
  const byQueue = (a, b) => (b.priorityAt || 0) - (a.priorityAt || 0) || b.updatedAt - a.updatedAt;
  const inScope = t => !pageOnly || (t.page || '').split('?')[0] === location.pathname;
  // fb-119: "awaiting response" = the last message is mine (Claude) — the ball is in the owner's court.
  const awaiting = t => { const last = t.thread && t.thread[t.thread.length - 1]; return !!last && last.author !== 'owner'; };
  const inFilter = t => inScope(t) && (!awaitingOnly || awaiting(t));
  const resolved = active.filter(t => t.status === 'resolved' && inFilter(t)).sort(byQueue);
  const openT = active.filter(t => t.status === 'open' && inFilter(t) && !fixedOnly).sort(byQueue);
  const held = db.tickets.filter(t => t.status === 'held').sort(byQueue);
  const history = db.tickets.filter(t => t.status === 'confirmed' || t.status === 'discarded').sort((a, b) => b.updatedAt - a.updatedAt);
  const attention = resolved.length; // items awaiting the owner's confirm
  const working = !!db.workingId && active.some(t => t.id === db.workingId);
  const pingSeen = db.pingAt && (db.claudeAckAt || 0) >= db.pingAt;

  const cardProps = { busy, onReplyChange, onReply, onConfirm, onPrioritize, onDiscard, onHold, onRevive, onGoto, onEditChange, onStartEdit, onSaveEdit, onCancelEdit, onFocus, onBlur };

  return <>
    <button data-borderless="chip" onClick={() => setOpen(o => !o)} title="Feedback (Dev mode)" className="feedback-launcher"
      style={{ position: 'fixed', right: 18, bottom: 18, zIndex: 90, borderRadius: 24, cursor: 'pointer',
        background: C.green, color: '#fff', border: 'none', boxShadow: '0 6px 18px rgba(0,0,0,.22)',
        padding: '10px 16px', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
      💬 Feedback
      {attention > 0 && <span style={{ background: '#fff', color: C.green, borderRadius: 20, fontSize: 11,
        fontWeight: 800, padding: '1px 7px' }}>{attention}</span>}
    </button>

    {open && <>
      <div onClick={() => setOpen(false)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,20,.34)', zIndex: 95 }} />
      <aside role="dialog" aria-label="Feedback" style={{ position: 'fixed', top: 0, right: 0, height: '100%',
        width: 'min(440px,96vw)', background: C.bg, borderLeft: `1px solid ${C.line}`, zIndex: 96,
        display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 30px rgba(0,0,0,.18)' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.line}`, background: C.panel,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: C.mut }}>Dev mode</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.ink }}>Feedback</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={onPing} disabled={busy}
              title={working ? 'Claude is working — click to re-ping' : pingSeen ? 'Ping received — click to re-ping' : 'Ask Claude to review the queue now'}
              style={{ ...btn, background: working ? C.amberBg : (pingSeen || pinged) ? C.greenBg : '#fff',
                color: working ? C.amber : C.green, border: `1px solid ${working ? C.amber : C.green}` }}>
              {working ? '⚙️ Claude working' : pinged ? '🔔 Pinged…' : pingSeen ? '✓ Ping received' : '🔔 Ask Claude'}
            </button>
            <button onClick={() => setOpen(false)} style={{ ...btn, ...btnGhost, width: 30, height: 30, padding: 0 }}>✕</button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
          {/* quick enter */}
          <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 11, padding: 11, marginBottom: 14 }}>
            <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={3}
              onFocus={onFocus} onBlur={onBlur}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
              placeholder="What needs fixing on this page?"
              style={{ ...inp, width: '100%', resize: 'vertical', minHeight: 58 }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button type="button" onClick={() => setScope('page')}
                style={{ ...segBtn, ...(scope === 'page' ? segOn : segOff) }}>📍 This page</button>
              <button type="button" onClick={() => setScope('general')}
                style={{ ...segBtn, ...(scope === 'general' ? segOn : segOff) }}>🌐 General</button>
            </div>
            {scope === 'page' && <div style={{ fontSize: 11, color: C.mut, marginTop: 6, wordBreak: 'break-all' }}>{location.pathname}{location.search}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={send} disabled={busy || !draft.trim()}
                style={{ ...btn, background: C.green, color: '#fff', border: `1px solid ${C.green}`, opacity: (busy || !draft.trim()) ? .5 : 1 }}>
                Send feedback
              </button>
            </div>
          </div>

          {/* fb-119: filters govern BOTH Fixed and In progress — moved here, right below the entry box. */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            <button type="button" onClick={() => setPageOnly(v => !v)} title="Show only tickets logged on this page (Fixed + In progress)"
              style={{ ...segBtn, flex: 'none', padding: '5px 11px', fontSize: 11.5, ...(pageOnly ? segOn : segOff) }}>📍 This page</button>
            <button type="button" onClick={() => setAwaitingOnly(v => !v)} title="Only tickets where I replied and am waiting on your confirmation"
              style={{ ...segBtn, flex: 'none', padding: '5px 11px', fontSize: 11.5, ...(awaitingOnly ? segOn : segOff) }}>💬 Awaiting response</button>
            <button type="button" onClick={() => setFixedOnly(v => !v)} title="Only Fixed tickets (composes with This page / Awaiting)"
              style={{ ...segBtn, flex: 'none', padding: '5px 11px', fontSize: 11.5, ...(fixedOnly ? segOn : segOff) }}>✓ Fixed</button>
          </div>

          {err && <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 8, padding: '7px 10px', marginBottom: 12 }}>{err}</div>}

          {resolved.length > 0 && <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em',
            textTransform: 'uppercase', color: C.green, margin: '4px 0 8px' }}>Fixed — please confirm ({resolved.length})</div>}
          {resolved.map(t => <Card key={t.id} t={t} active={t.id === db.workingId} editing={editingId === t.id} editVal={editVal} replyVal={replies[t.id]} {...cardProps} />)}

          {(openT.length > 0 || pageOnly || awaitingOnly) && !fixedOnly && <div style={{ margin: '12px 0 8px' }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: C.amber }}>In progress ({openT.length})</span>
          </div>}
          {openT.map(t => <Card key={t.id} t={t} active={t.id === db.workingId} editing={editingId === t.id} editVal={editVal} replyVal={replies[t.id]} {...cardProps} />)}

          {active.length === 0 && !err && <div style={{ fontSize: 13, color: C.mut, textAlign: 'center', padding: '24px 0' }}>
            Nothing open. 🎉</div>}

          {held.length > 0 && <>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: C.mut, margin: '14px 0 8px', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setShowHeld(h => !h)}>
              <span>On hold ({held.length})</span><span>{showHeld ? '\u2013' : '+'}</span>
            </div>
            {showHeld && held.map(t => <Card key={t.id} t={t} editing={editingId === t.id} editVal={editVal} replyVal={replies[t.id]} {...cardProps} />)}
          </>}

          {history.length > 0 && <>
            <button onClick={() => setShowHistory(h => !h)} style={{ ...btn, ...btnGhost, marginTop: 16, width: '100%' }}>
              {showHistory ? 'Hide' : 'Show'} history ({history.length})
            </button>
            {showHistory && <div style={{ marginTop: 10, opacity: .8 }}>{history.map(t => <Card key={t.id} t={t} editing={editingId === t.id} editVal={editVal} replyVal={replies[t.id]} {...cardProps} />)}</div>}
          </>}
        </div>
      </aside>
    </>}
  </>;
}

const btn = { padding: '6px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { background: '#fff', color: C.ink, border: `1px solid ${C.line}` };
const segBtn = { flex: 1, padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const segOn = { background: C.greenBg, color: C.green, border: `1px solid ${C.green}` };
const segOff = { background: '#fff', color: C.mut, border: `1px solid ${C.line}` };
const inp = { padding: '7px 10px', borderRadius: 8, border: `1px solid ${C.line}`, fontSize: 13, flex: 1,
  fontFamily: 'inherit', color: C.ink, background: '#fff', outline: 'none' };
